<script lang="ts">
export interface DataTableVirtualizer extends ClassProps {
	height: number;
	estimateSize?: number;
	overscan?: number;
}

export type DataTableVirtualizerContext<TData> = {
	topPadding: Ref<number>;
	bottomPadding: Ref<number>;
	virtualRows: Ref<DataTableRow<TData>[]>;
};

export const [injectDataTableVirtualizerContext, provideDataTableVirtualizerContext] =
	createContext<DataTableVirtualizerContext<any>>("DataTableVirtualizer");
</script>

<script setup lang="ts" generic="TData extends RowData">
import type { RowData } from "@tanstack/vue-table";
import { useVirtualizer } from "@tanstack/vue-virtual";
import { createContext, useForwardExpose } from "reka-ui";
import { computed, onUnmounted, watch, type Ref } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import { injectDataCollectionContext } from "../data-collection/composables/data-collection";
import { injectDataTableRootContext, type DataTableRow } from "./DataTableRoot.vue";

const { items } = injectDataCollectionContext();
const { virtual } = injectDataTableRootContext();
virtual.value.enabled = true;
onUnmounted(() => {
	virtual.value.enabled = false;
});

const props = withDefaults(defineProps<DataTableVirtualizer>(), {
	estimateSize: 40,
	overscan: 5,
});

const { forwardRef, currentRef } = useForwardExpose();
const currentElement = currentRef as Ref<Element | null>;

defineSlots<{
	default(slotProps: { height: number }): void;
}>();

const padding = computed(() => {
	const el = currentElement.value;
	if (!el) {
		return { start: 0, end: 0 };
	} else {
		const styles = window.getComputedStyle(el);
		return {
			start: Number.parseFloat(styles.paddingBlockStart || styles.paddingTop),
			end: Number.parseFloat(styles.paddingBlockEnd || styles.paddingBottom),
		};
	}
});

const rowVirtualizer = useVirtualizer(
	computed(() => ({
		count: items.value.length,
		estimateSize: () => props.estimateSize,
		getScrollElement: () => currentElement.value,
		getItemKey: (idx: number) => items.value[idx].row.id,
		overscan: props.overscan,
		get scrollPaddingStart() {
			return padding.value.start;
		},
		get scrollPaddingEnd() {
			return padding.value.end;
		},
	}))
);

watch(
	() => rowVirtualizer?.value.getTotalSize(),
	(size) => {
		if (size != null) {
			virtual.value.totalSize = size;
		}
	},
	{ immediate: true }
);

const virtualRows = computed(() => {
	const virtualRows: DataTableRow<TData>[] = [];

	for (const item of rowVirtualizer.value.getVirtualItems()) {
		virtualRows.push({
			row: items.value[item.index].row,
			style: {
				height: `${item.size}px`,
			},
		});
	}

	return virtualRows;
});

const topPadding = computed(() => {
	const firstItem = rowVirtualizer.value.getVirtualItems().at(0);
	if (firstItem == null) {
		return 0;
	}

	return firstItem.start;
});

const bottomPadding = computed(() => {
	const lastItem = rowVirtualizer.value.getVirtualItems().at(-1);
	if (lastItem == null) {
		return 0;
	}

	return rowVirtualizer.value.getTotalSize() - lastItem.end;
});

provideDataTableVirtualizerContext({
	topPadding,
	bottomPadding,
	virtualRows,
});
</script>

<template>
	<div
		:ref="forwardRef"
		:class="cn('overflow-auto', props.class)"
		:style="{
			height: `${height}px`,
		}"
	>
		<div
			:style="{
				height: `${rowVirtualizer.getTotalSize()}px`,
			}"
		>
			<slot :height="rowVirtualizer.getTotalSize()" />
		</div>
	</div>
</template>
